import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import {
  createConsumer,
  type Consumer,
  type Subscription,
} from "@rails/actioncable";
import { appendQuotes } from "@/lib/quote-cache";
import { useAuth } from "@/hooks/useAuth";
import type { LiveQuote } from "@/types/market";

// Single Action Cable consumer for the entire app. Three channels hang
// off it: UserChannel (always while authed), MarketDataChannel (per-ticker
// live-quote stream, refcounted per ticker), and OptionChainChannel
// (refcounted per ticker). The consumer is created and torn down by
// UserChannelProvider in lockstep with auth status — when the session
// expires, the server tears down the WebSocket; we mirror that by
// disconnecting the consumer and re-create a fresh one on re-login so
// the new session cookie is used. Listener registries persist across
// disconnect/connect, so individual components don't have to react to
// auth themselves; their subscriptions get re-attached automatically.

export type UserChannelMessage = {
  event: string;
  [key: string]: unknown;
};

export interface OptionChainChainBroadcast {
  event: "chain";
  ticker: string;
  expiry: string;
  snapshot_at: string;
  underlying_spot: number;
  atm_iv: number | null;
  atm_strike?: number | null;
  atm_call_iv?: number | null;
  atm_put_iv?: number | null;
  atm_straddle_mid?: number | null;
  contract_count: number;
}

export interface OptionChainIv30dBroadcast {
  event: "iv_30d";
  ticker: string;
  bar_at: string;
  iv_30d: number;
  anchor_expiry_short: string;
  anchor_expiry_long: string;
  weight_short: number;
}

export type OptionChainBroadcast =
  | OptionChainChainBroadcast
  | OptionChainIv30dBroadcast;

export interface MarketDataMessage {
  event: "quote";
  ticker: string;
  price: number;
  ts_micros: number;
  timestamp: string;
  source: string;
}

type MessageHandler = (message: UserChannelMessage) => void;
type OptionChainListener = (msg: OptionChainBroadcast) => void;
type LiveQuoteListener = (q: LiveQuote) => void;
type MarketDataListener = (msg: MarketDataMessage) => void;

type UserChannelContextValue = {
  subscribe: (handler: MessageHandler) => () => void;
  perform: (action: string, data?: Record<string, unknown>) => void;
};

const UserChannelContext = createContext<UserChannelContextValue>(null!);

let consumer: Consumer | null = null;
let userSubscription: Subscription | null = null;
let connectedForStatus: string | null = null;

const userHandlers = new Set<MessageHandler>();
const latestQuoteByTicker = new Map<string, LiveQuote>();

interface OptionChainEntry {
  sub: Subscription | null;
  listeners: Set<OptionChainListener>;
  refcount: number;
}
const optionChainEntries = new Map<string, OptionChainEntry>();

interface MarketDataEntry {
  sub: Subscription | null;
  listeners: Set<MarketDataListener>;
  refcount: number;
}
const marketDataEntries = new Map<string, MarketDataEntry>();

function attachUserChannel() {
  if (!consumer || userSubscription) return;
  userSubscription = consumer.subscriptions.create("UserChannel", {
    received(data: UserChannelMessage) {
      userHandlers.forEach((handler) => handler(data));
    },
  });
}

function attachOptionChainEntry(ticker: string, entry: OptionChainEntry) {
  if (!consumer || entry.sub) return;
  entry.sub = consumer.subscriptions.create(
    { channel: "OptionChainChannel", ticker },
    {
      received(data: OptionChainBroadcast) {
        if (data?.event !== "chain" && data?.event !== "iv_30d") return;
        entry.listeners.forEach((listener) => listener(data));
      },
    },
  );
}

function attachMarketDataEntry(ticker: string, entry: MarketDataEntry) {
  if (!consumer || entry.sub) return;
  entry.sub = consumer.subscriptions.create(
    { channel: "MarketDataChannel", ticker },
    {
      received(data: MarketDataMessage) {
        if (data?.event !== "quote") return;
        latestQuoteByTicker.set(data.ticker, {
          ticker: data.ticker,
          price: data.price,
          ts_micros: data.ts_micros,
          source: data.source,
        });
        appendQuotes(data.ticker, [
          {
            id: data.ts_micros,
            ticker: data.ticker,
            price: data.price,
            timestamp: new Date(data.ts_micros / 1000).toISOString(),
            source: data.source,
          },
        ]);
        entry.listeners.forEach((cb) => cb(data));
      },
    },
  );
}

function connect() {
  if (consumer) return;
  consumer = createConsumer();
  attachUserChannel();
  for (const [ticker, entry] of optionChainEntries) {
    attachOptionChainEntry(ticker, entry);
  }
  for (const [ticker, entry] of marketDataEntries) {
    attachMarketDataEntry(ticker, entry);
  }
}

function disconnect() {
  userSubscription?.unsubscribe();
  userSubscription = null;
  for (const entry of optionChainEntries.values()) {
    entry.sub?.unsubscribe();
    entry.sub = null;
  }
  for (const entry of marketDataEntries.values()) {
    entry.sub?.unsubscribe();
    entry.sub = null;
  }
  consumer?.disconnect();
  consumer = null;
  connectedForStatus = null;
}

export function subscribeOptionChain(
  ticker: string,
  cb: OptionChainListener,
): () => void {
  const key = ticker.toUpperCase();
  let entry = optionChainEntries.get(key);
  if (!entry) {
    entry = { sub: null, listeners: new Set(), refcount: 0 };
    optionChainEntries.set(key, entry);
  }
  entry.listeners.add(cb);
  entry.refcount += 1;
  attachOptionChainEntry(key, entry);

  return () => {
    const current = optionChainEntries.get(key);
    if (!current) return;
    current.listeners.delete(cb);
    current.refcount -= 1;
    if (current.refcount <= 0) {
      current.sub?.unsubscribe();
      optionChainEntries.delete(key);
    }
  };
}

export function subscribeMarketData(
  ticker: string,
  cb: MarketDataListener,
): () => void {
  const key = ticker.toUpperCase();
  let entry = marketDataEntries.get(key);
  if (!entry) {
    entry = { sub: null, listeners: new Set(), refcount: 0 };
    marketDataEntries.set(key, entry);
  }
  entry.listeners.add(cb);
  entry.refcount += 1;
  attachMarketDataEntry(key, entry);

  return () => {
    const current = marketDataEntries.get(key);
    if (!current) return;
    current.listeners.delete(cb);
    current.refcount -= 1;
    if (current.refcount <= 0) {
      current.sub?.unsubscribe();
      marketDataEntries.delete(key);
    }
  };
}

export function subscribeLiveQuote(
  ticker: string,
  cb: LiveQuoteListener,
): () => void {
  const cached = latestQuoteByTicker.get(ticker);
  if (cached) cb(cached);
  return subscribeMarketData(ticker, (msg) => {
    cb({
      ticker: msg.ticker,
      price: msg.price,
      ts_micros: msg.ts_micros,
      source: msg.source,
    });
  });
}

export function __resetCablesForTests(): void {
  disconnect();
  userHandlers.clear();
  latestQuoteByTicker.clear();
  optionChainEntries.clear();
  marketDataEntries.clear();
}

export function UserChannelProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const handlersRef = useRef(userHandlers);

  useEffect(() => {
    if (status === "authenticated" && connectedForStatus !== "authenticated") {
      if (connectedForStatus === "unauthenticated") disconnect();
      connect();
      connectedForStatus = "authenticated";
    } else if (
      status === "unauthenticated" &&
      connectedForStatus !== "unauthenticated"
    ) {
      disconnect();
      connectedForStatus = "unauthenticated";
    }
  }, [status]);

  useEffect(() => {
    return () => disconnect();
  }, []);

  const subscribe = useCallback((handler: MessageHandler) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  const perform = useCallback(
    (action: string, data?: Record<string, unknown>) => {
      userSubscription?.perform(action, data);
    },
    [],
  );

  const value = useMemo(() => ({ subscribe, perform }), [subscribe, perform]);

  return (
    <UserChannelContext.Provider value={value}>
      {children}
    </UserChannelContext.Provider>
  );
}

export function useUserChannel() {
  return useContext(UserChannelContext);
}
